"use client";

import * as React from "react";
import Section, { Reveal } from "../layout/Section";
import {
  EDUCATION,
  EXPERIENCE,
  THROUGH_LINE,
  type Role,
} from "../../data/experience";

/** Renders **bold** spans. The data is authored, so this is enough. */
function inline(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    const m = part.match(/^\*\*([^*]+)\*\*$/);
    return m ? (
      <strong key={i} className="font-medium text-ink">
        {m[1]}
      </strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    );
  });
}

export default function Experience() {
  return (
    <Section
      id="experience"
      index="02"
      title="Experience"
      lede="Six years across three companies: a small consultancy doing government work, an enterprise org, and a studio I co-founded."
    >
      <ol className="space-y-px overflow-hidden rounded-xl border bg-[rgb(var(--line)/var(--line-a))]">
        {EXPERIENCE.map((role, i) => (
          <Reveal key={role.company} delay={i * 60}>
            <RoleRow role={role} />
          </Reveal>
        ))}
      </ol>

      {/* The synthesis. Three places produced three different capabilities;
          this is where they add up to a claim rather than a list. */}
      <Reveal className="mt-14">
        <div className="rounded-xl border bg-surface p-6 md:p-10">
          <p className="eyebrow">{THROUGH_LINE.heading}</p>

          {/* Two axes across the same three jobs: team shape on top, the
              engineering underneath. Read across, not down. */}
          <ol className="mt-6 grid gap-px overflow-hidden rounded-lg border bg-[rgb(var(--line)/var(--line-a))] md:grid-cols-3">
            {[...EXPERIENCE].reverse().map((role, i) => (
              <li key={role.company} className="flex flex-col bg-surface p-4">
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-2xs text-faint">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[0.8125rem] font-medium text-ink">
                    {role.company}
                  </span>
                </div>

                <dl className="mt-3 space-y-1.5">
                  <div className="flex items-baseline gap-2">
                    <dt className="w-14 shrink-0 font-mono text-[10px] uppercase tracking-wider text-faint">
                      Team
                    </dt>
                    <dd className="text-2xs text-muted">{role.arc.team}</dd>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <dt className="w-14 shrink-0 font-mono text-[10px] uppercase tracking-wider text-faint">
                      Work
                    </dt>
                    <dd className="text-2xs text-accent">
                      {role.arc.discipline}
                    </dd>
                  </div>
                </dl>

                <p className="prose-body mt-3 text-[0.8125rem]">
                  {role.arc.body}
                </p>
              </li>
            ))}
          </ol>

          <div className="mt-7 max-w-3xl space-y-3">
            {THROUGH_LINE.body.map((para, i) => (
              <p key={i} className="prose-body text-[0.9375rem]">
                {para}
              </p>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal className="mt-12">
        <p className="eyebrow">Education</p>
        <ul className="mt-4 grid gap-4 sm:grid-cols-2">
          {EDUCATION.map((e) => (
            <li key={e.institution} className="flex flex-col gap-0.5">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4">
                <h3 className="text-base font-medium">{e.institution}</h3>
                <span className="font-mono text-2xs text-faint">
                  {e.period}
                </span>
              </div>
              <p className="text-sm text-muted">{e.credential}</p>
            </li>
          ))}
        </ul>
      </Reveal>
    </Section>
  );
}

function RoleRow({ role }: { role: Role }) {
  const [open, setOpen] = React.useState(false);
  const id = `${role.company.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-detail`;

  return (
    <li className="bg-bg p-5 md:p-8">
      <div className="grid gap-5 md:grid-cols-[13rem_minmax(0,1fr)] md:gap-10">
        <div>
          <h3 className="text-lg font-semibold tracking-tight">
            {role.company}
          </h3>
          <p className="mt-1 text-sm text-ink">{role.role}</p>
          <p className="mt-2 font-mono text-2xs text-faint">
            {role.period}
            <br />
            {role.location}
          </p>
        </div>

        {/* The row reads as a timeline entry by default: what the job was, in
            one line, and where the full story lives. The bullets restate the
            case studies, so they stay folded for anyone who has just read
            them and open for anyone who wants the resume version. */}
        <div>
          <p className="text-[0.9375rem] leading-relaxed text-muted">
            {role.scope}
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {role.tech.map((t) => (
              <span key={t} className="chip">
                {t}
              </span>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
            {role.caseStudy && (
              <a href={`#${role.caseStudy}`} className="link text-sm">
                Read the case study ↑
              </a>
            )}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls={id}
              className="group flex items-center gap-2 text-sm text-muted transition-colors hover:text-ink"
            >
              {open ? "Hide highlights" : `${role.bullets.length} highlights`}
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                aria-hidden
                className="text-faint transition-transform duration-300 group-hover:text-ink"
                style={{ transform: open ? "rotate(180deg)" : undefined }}
              >
                <path
                  d="M4 6.5 8 10.5 12 6.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {open && (
            <div id={id}>
              <ul className="mt-5 space-y-2.5">
                {role.bullets.map((b, i) => (
                  <li
                    key={i}
                    className="grid grid-cols-[0.75rem_minmax(0,1fr)] gap-2 text-[0.9375rem] leading-relaxed text-muted"
                  >
                    <span
                      className="mt-[0.6em] h-px w-2 bg-[rgb(var(--line)/0.35)]"
                      aria-hidden
                    />
                    <span>{inline(b)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
